import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../dimensions';

// Pure-JS simulation half of the lava field — no Phaser imports so it's unit-testable.
// The Phaser-side renderer (Background.ts) creates a Shader GameObject whose fragment
// shader sums a metaball field across these blobs every frame on the GPU.

export const RISING_BLOB_COUNT = 10;
export const BED_BLOB_COUNT = 6;
export const BLOB_COUNT = RISING_BLOB_COUNT + BED_BLOB_COUNT;

// A rising blob whose top edge has drifted this far past the viewport's top is recycled.
const RESPAWN_TOP_MARGIN_PX = 80;
const RESPAWN_BAND_MIN_PX = 40;
const RESPAWN_BAND_MAX_PX = 280;
// Seconds for a respawned blob to grow from a seed point to its full radius. Radius is used
// rather than alpha because the metaball threshold step would collapse soft alpha edges.
const FADE_IN_SECONDS = 0.7;

export interface Blob {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  anchored: boolean;
  // Anchored-only: world-fixed rest position + per-blob phase for the breathing wobble.
  baseX: number;
  baseY: number;
  phaseOffset: number;
  // Fade-in progress, 0..1. Initial blobs start at 1; respawned rising blobs start at 0.
  fade: number;
}

export function initBlobs(bedBaseY: number, initialViewBottom: number): Blob[] {
  const blobs: Blob[] = [];

  // Bed: large blobs along a fixed world-y, gently swaying. Lives at world bottom for the
  // Game scene (so it drops out of view as the player climbs) and at viewport bottom for
  // the Title scene (where there's no scrolling).
  const slot = LOGICAL_WIDTH / BED_BLOB_COUNT;
  for (let i = 0; i < BED_BLOB_COUNT; i++) {
    const baseX = slot * (i + 0.5) + (Math.random() - 0.5) * slot * 0.4;
    const baseY = bedBaseY + (Math.random() - 0.5) * 24;
    blobs.push({
      x: baseX, y: baseY,
      vx: 0, vy: 0,
      r: 55 + Math.random() * 35,
      anchored: true,
      baseX, baseY,
      phaseOffset: Math.random() * Math.PI * 2,
      fade: 1,
    });
  }

  const initialViewTop = initialViewBottom - LOGICAL_HEIGHT;
  for (let i = 0; i < RISING_BLOB_COUNT; i++) {
    blobs.push({
      x: Math.random() * LOGICAL_WIDTH,
      y: initialViewTop + Math.random() * (LOGICAL_HEIGHT + 160),
      vx: (Math.random() - 0.5) * 6,
      vy: -(15 + Math.random() * 22),
      r: 28 + Math.random() * 30,
      anchored: false,
      baseX: 0, baseY: 0,
      phaseOffset: 0,
      fade: 1,
    });
  }

  return blobs;
}

function respawnRisingBlob(blob: Blob, viewBottom: number): void {
  blob.x = Math.random() * LOGICAL_WIDTH;
  blob.y = viewBottom + RESPAWN_BAND_MIN_PX + Math.random() * (RESPAWN_BAND_MAX_PX - RESPAWN_BAND_MIN_PX);
  blob.vx = (Math.random() - 0.5) * 6;
  blob.vy = -(15 + Math.random() * 22);
  blob.r = 28 + Math.random() * 30;
  blob.fade = 0;
}

export function updateBlobs(blobs: Blob[], dt: number, timeMs: number, scrollY: number): void {
  const t = timeMs * 0.0003;
  const viewBottom = scrollY + LOGICAL_HEIGHT;
  for (const blob of blobs) {
    if (blob.anchored) {
      blob.x = blob.baseX + Math.sin(t + blob.phaseOffset) * 12;
      blob.y = blob.baseY + Math.cos(t + blob.phaseOffset * 0.7) * 6;
      continue;
    }
    blob.x += blob.vx * dt;
    blob.y += blob.vy * dt;
    if (blob.x < blob.r) blob.vx = Math.abs(blob.vx);
    else if (blob.x > LOGICAL_WIDTH - blob.r) blob.vx = -Math.abs(blob.vx);

    if (blob.fade < 1) blob.fade = Math.min(1, blob.fade + dt / FADE_IN_SECONDS);

    if (blob.y - scrollY < -blob.r * 2 - RESPAWN_TOP_MARGIN_PX) {
      respawnRisingBlob(blob, viewBottom);
      continue;
    }
    if (blob.y - viewBottom > RESPAWN_BAND_MAX_PX + blob.r * 2) {
      respawnRisingBlob(blob, viewBottom);
    }
  }
}

/**
 * Packs blob state into a flat `vec3[BLOB_COUNT]` uniform buffer (length BLOB_COUNT*3):
 * `(screenX, screenY, effectiveRadius)` per blob, in logical pixels with y=0 at the top
 * of the viewport. Blobs that cannot influence any visible fragment get radius=0 so the
 * shader's per-fragment loop sees them as inactive contributors. The caller owns `out`
 * and re-uses it across frames (Phaser's uniform setter only re-uploads when values change).
 */
export function packBlobsForShader(
  blobs: ReadonlyArray<Blob>,
  scrollY: number,
  out: Float32Array,
): void {
  for (let i = 0; i < blobs.length; i++) {
    const b = blobs[i];
    const screenY = b.y - scrollY;
    const r = b.fade <= 0 ? 0 : b.r * (0.35 + 0.65 * b.fade);
    const visible = screenY > -r * 2 && screenY < LOGICAL_HEIGHT + r * 2;
    const base = i * 3;
    out[base]     = b.x;
    out[base + 1] = screenY;
    out[base + 2] = visible ? r : 0;
  }
}
